import { useMutation } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem, GridRowHeightParams } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CommandButton } from "components/CommandButton";
import { ConfirmDialog } from "components/ConfirmDialog";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TruncateTypography } from "components/TruncateTypography";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  WorkflowManagerConfigRulesQuery,
  WorkflowManagerConfigType,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { groupBy } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { prettyPrintDateMed } from "util/dates";

import { AddRuleModal } from "./AddRuleModal";
import { useWorkflowConfigAuth } from "./useWorkflowConfigAuth";

export const WORKFLOW_MANAGER_CONFIG_RULES_QUERY = gql(`
  query WorkflowManagerConfigRules($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    workflowManagerConfigRules(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        id
        type
        value
        humanReadableValue
        notes
        createdAt
        createdBy
        updatedAt
        updatedBy
        facility {
          id
          name
        }
        tenant {
          id
          name
        }
      }
    }
  }
`);

const DELETE_WORKFLOW_MANAGER_CONFIG_RULE_MUTATION = gql(`
  mutation DeleteWorkflowManagerConfigRule($id: ID!) {
    deleteWorkflowManagerConfigRule(id: $id)
  }
`);

export type WorkflowConfigRuleRow =
  WorkflowManagerConfigRulesQuery["workflowManagerConfigRules"]["items"][number];

type SectionName =
  | "States"
  | "State Revisions"
  | "Facilities"
  | "Facility Revisions"
  | "Tenants"
  | "Other Config";

type SectionHeaderRow = {
  __isSectionHeader: true;
  __groupName: SectionName;
  id: string;
};

type GroupedRow = WorkflowConfigRuleRow | SectionHeaderRow;

function isWorkflowConfigRuleRow(row: GroupedRow): row is WorkflowConfigRuleRow {
  return !("__isSectionHeader" in row && row.__isSectionHeader);
}

const getTypeEmojiAndTooltip = (
  type: WorkflowManagerConfigType,
  value: string
): { emoji: string; tooltip: string } => {
  // For ALLOW_STATE, ALLOW_STATE_REVISION, and ALLOW_STATE_NEWLY_NOT_REPORTING, check value patterns
  if (
    type === WorkflowManagerConfigType.AllowState ||
    type === WorkflowManagerConfigType.AllowStateRevision ||
    type === WorkflowManagerConfigType.AllowStateNewlyNotReporting
  ) {
    if (value.endsWith("_NEEDS_CANDIDATE")) {
      return {
        emoji: "🟠",
        tooltip: "Blocked - needs test candidate",
      };
    }
    if (value.endsWith("_NOT_IMPLEMENTED")) {
      return {
        emoji: "🔴",
        tooltip: "Blocked - not implemented",
      };
    }
    // Regular 2-letter state code
    return {
      emoji: "🟢",
      tooltip: "Allowed",
    };
  }

  // For other rule types
  switch (type) {
    case WorkflowManagerConfigType.DenyFacility:
    case WorkflowManagerConfigType.DenyTenant:
    case WorkflowManagerConfigType.DenyTenantForState:
    case WorkflowManagerConfigType.DenyFacilityRevision:
    case WorkflowManagerConfigType.DenyTenantRevision:
    case WorkflowManagerConfigType.DenyTenantForStateRevision:
    case WorkflowManagerConfigType.DenyTenantNewlyNotReporting:
    case WorkflowManagerConfigType.DenyTenantForStateNewlyNotReporting:
    case WorkflowManagerConfigType.DenyFacilityNewlyNotReporting:
    case WorkflowManagerConfigType.DenyLobMailToSerc:
      return {
        emoji: "⛔",
        tooltip: "Explicitly denied",
      };
    case WorkflowManagerConfigType.AllowFacility:
    case WorkflowManagerConfigType.AllowFacilityRevision:
    case WorkflowManagerConfigType.AllowFacilityNewlyNotReporting:
      return {
        emoji: "🟢",
        tooltip: "Allowed",
      };
    case WorkflowManagerConfigType.StateBypassValidation:
    case WorkflowManagerConfigType.FacilityBypassValidation:
      return {
        emoji: "🔑",
        tooltip: "Allowed but skips validation",
      };
    case WorkflowManagerConfigType.ForceReportTypeForSubmission:
    case WorkflowManagerConfigType.TestingFailureRate:
    case WorkflowManagerConfigType.WebAutomationStateOverrideUseAa:
    case WorkflowManagerConfigType.Version:
    case WorkflowManagerConfigType.SchedulerWorkIntervalSleepTimeMs:
    case WorkflowManagerConfigType.SchedulerConsumerIntervalSleepTimeMs:
    case WorkflowManagerConfigType.SchedulerRunningPoolMaxCapacity:
    case WorkflowManagerConfigType.SchedulerDbAcuCutoffPercent:
    case WorkflowManagerConfigType.SchedulerErrorRateCutoffPercent:
    case WorkflowManagerConfigType.SchedulerCapacityWaitDelayMs:
    case WorkflowManagerConfigType.SchedulerLongRunningThresholdMs:
    case WorkflowManagerConfigType.SchedulerMetricsCacheDurationMs:
    case WorkflowManagerConfigType.SchedulerLambdaMetricsWindowMs:
    case WorkflowManagerConfigType.SchedulerMinErrorSamples:
    case WorkflowManagerConfigType.SchedulerCohortCloseIntervalMs:
    case WorkflowManagerConfigType.AllowEnvironment:
      return {
        emoji: "⚙️",
        tooltip: "System configuration",
      };
    default:
      return {
        emoji: "⚠️",
        tooltip: "Unknown Format (WARNING)",
      };
  }
};

const getGroupForType = (type: WorkflowManagerConfigType): SectionName => {
  switch (type) {
    case WorkflowManagerConfigType.AllowState:
    case WorkflowManagerConfigType.AllowStateNewlyNotReporting:
      return "States";
    case WorkflowManagerConfigType.AllowStateRevision:
      return "State Revisions";
    case WorkflowManagerConfigType.AllowFacility:
    case WorkflowManagerConfigType.DenyFacility:
    case WorkflowManagerConfigType.AllowFacilityNewlyNotReporting:
    case WorkflowManagerConfigType.DenyFacilityNewlyNotReporting:
      return "Facilities";
    case WorkflowManagerConfigType.AllowFacilityRevision:
    case WorkflowManagerConfigType.DenyFacilityRevision:
      return "Facility Revisions";
    case WorkflowManagerConfigType.DenyTenant:
    case WorkflowManagerConfigType.DenyTenantRevision:
    case WorkflowManagerConfigType.DenyTenantForState:
    case WorkflowManagerConfigType.DenyTenantForStateRevision:
    case WorkflowManagerConfigType.DenyTenantNewlyNotReporting:
    case WorkflowManagerConfigType.DenyTenantForStateNewlyNotReporting:
      return "Tenants";
    default:
      return "Other Config";
  }
};

const groupRowsByType = (
  items: WorkflowConfigRuleRow[],
  sectionExpanded: Record<SectionName, boolean>
): GroupedRow[] => {
  // Group items by their type category
  const grouped = groupBy(items, (item) => getGroupForType(item.type));

  // Define the order of groups
  const groupOrder: SectionName[] = [
    "States",
    "State Revisions",
    "Facilities",
    "Facility Revisions",
    "Tenants",
    "Other Config",
  ];

  const result: GroupedRow[] = [];

  for (const groupName of groupOrder) {
    const groupItems = grouped[groupName] || [];
    if (groupItems.length > 0) {
      // Add section header
      result.push({
        __isSectionHeader: true,
        __groupName: groupName,
        id: `__section-header-${groupName}`,
      });
      // Only add items if section is expanded
      if (sectionExpanded[groupName] !== false) {
        result.push(...groupItems);
      }
    }
  }

  return result;
};

export function Rules() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const alerts = useAlerts();
  const { isAuthorized } = useWorkflowConfigAuth();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<WorkflowConfigRuleRow | null>(null);

  const [sectionExpanded, setSectionExpanded] = useLocalStorageState<{
    States: boolean;
    "State Revisions": boolean;
    Facilities: boolean;
    "Facility Revisions": boolean;
    Tenants: boolean;
    "Other Config": boolean;
  }>("workflow-config-rules-sections-expanded", {
    defaultValue: {
      States: true,
      "State Revisions": true,
      Facilities: true,
      "Facility Revisions": true,
      Tenants: true,
      "Other Config": true,
    },
  });

  const [deleteMutation, { loading: deleteLoading }] = useMutation(
    DELETE_WORKFLOW_MANAGER_CONFIG_RULE_MUTATION,
    {
      refetchQueries: [WORKFLOW_MANAGER_CONFIG_RULES_QUERY],
      onCompleted: () => {
        alerts.success("Rule deleted successfully");
        setRuleToDelete(null);
      },
      onError: (error) => {
        alerts.error(`Failed to delete rule: ${error.message}`);
      },
    }
  );

  const handleDelete = useCallback(() => {
    if (ruleToDelete) {
      return deleteMutation({
        variables: { id: ruleToDelete.id },
      });
    }
  }, [ruleToDelete, deleteMutation]);

  useBreadcrumb([
    {
      label: "Workflow Config",
      to: "/staff/workflow-config",
    },
    {
      label: "Rules",
    },
  ]);

  const columns = useMemo((): OmnisearchGridColDef<GroupedRow>[] => {
    const baseColumns: OmnisearchGridColDef<GroupedRow>[] = [
      {
        field: "type",
        headerName: "Type",
        width: isMobile ? 120 : 200,
        sortable: true,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(WorkflowManagerConfigType),
        enumPresentationFunction: (enumValue: string) => {
          const type = enumValue as WorkflowManagerConfigType;
          // For enum presentation, we don't have access to value, so use default emoji
          const { emoji } = getTypeEmojiAndTooltip(type, "");
          const prettyValue = prettyPrintEnumValue(type);
          return emoji ? `${emoji} ${prettyValue}` : prettyValue;
        },
        renderCell: ({ row }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            const groupName = row.__groupName as SectionName;
            const isExpanded = sectionExpanded[groupName] !== false;
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  width: "100%",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSectionExpanded((prev) => ({
                    ...prev,
                    [groupName]: !isExpanded,
                  }));
                }}
              >
                <IconButton size="small" sx={{ padding: 0.5 }}>
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
                <Typography
                  variant="subtitle2"
                  className="section-header-row-title"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                >
                  {groupName}
                </Typography>
              </Box>
            );
          }
          if (!isWorkflowConfigRuleRow(row)) {
            return null;
          }
          const { emoji, tooltip } = getTypeEmojiAndTooltip(row.type, row.value);
          const prettyValue = prettyPrintEnumValue(row.type);
          return (
            <Typography
              variant="body2"
              sx={{ py: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {emoji ? (
                <Tooltip title={tooltip} arrow>
                  <span style={{ cursor: "help" }}>{emoji}</span>
                </Tooltip>
              ) : null}
              {prettyValue}
            </Typography>
          );
        },
      },
      {
        field: "humanReadableValue",
        headerName: "Value",
        flex: isMobile ? 2 : 4,
        minWidth: 200,
        sortable: true,
        renderCell: ({ row, value }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return null; // Section header name shown in Type column
          }
          return (
            <Typography variant="body2" sx={{ py: 0.5 }}>
              {value}
            </Typography>
          );
        },
      },
      {
        field: "rawType",
        headerName: "Raw Type",
        width: 200,
        sortable: true,
        filterable: false,
        valueGetter: (_, row) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return "";
          }
          if (!isWorkflowConfigRuleRow(row)) {
            return "";
          }
          return row.type;
        },
        renderCell: ({ row, value }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return null;
          }
          return (
            <Typography variant="body2" sx={{ py: 0.5, fontFamily: "monospace" }}>
              {value}
            </Typography>
          );
        },
      },
      {
        field: "rawValue",
        headerName: "Raw Value",
        width: 200,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return "";
          }
          if (!isWorkflowConfigRuleRow(row)) {
            return "";
          }
          return row.value;
        },
        renderCell: ({ row, value }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return null;
          }
          return (
            <Typography variant="body2" sx={{ py: 0.5, fontFamily: "monospace" }}>
              {value}
            </Typography>
          );
        },
      },

      {
        field: "notes",
        headerName: "Notes",
        flex: 1,
        minWidth: 150,
        sortable: true,
        renderCell: ({ row, value }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return null;
          }
          return value ? (
            <Stack>
              <TruncateTypography
                variant="body2"
                sx={{
                  py: 0.5,
                }}
                showTooltip={true}
              >
                {value}
              </TruncateTypography>
            </Stack>
          ) : null;
        },
      },
      {
        field: "updatedBy",
        headerName: "Updated By",
        width: 120,
        sortable: true,
        renderCell: ({ row, value }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return null;
          }
          return value;
        },
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        width: 120,
        sortable: true,
        filterKeyType: "date",
        valueGetter: (_, row) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return "";
          }
          if (!isWorkflowConfigRuleRow(row)) {
            return "";
          }
          return prettyPrintDateMed(row.updatedAt);
        },
        renderCell: ({ row, value }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return null;
          }
          return value;
        },
      },
      {
        field: "createdAt",
        headerName: "Created At",
        width: 120,
        sortable: true,
        filterKeyType: "date",
        valueGetter: (_, row) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return "";
          }
          if (!isWorkflowConfigRuleRow(row)) {
            return "";
          }
          return prettyPrintDateMed(row.createdAt);
        },
        renderCell: ({ row, value }) => {
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return null;
          }
          return value;
        },
      },
    ];

    // Always show actions column, but disable when not authorized
    baseColumns.push({
      field: "actions",
      type: "actions",
      headerName: "",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      getActions: (params) => {
        // Don't show actions for section headers or "Other Config" items
        if ("__isSectionHeader" in params.row && params.row.__isSectionHeader) {
          return [];
        }

        if (!isWorkflowConfigRuleRow(params.row)) {
          return [];
        }

        const group = getGroupForType(params.row.type);
        if (group === "Other Config") {
          return [];
        }

        const tooltipTitle = isAuthorized
          ? "Remove Rule"
          : "You don't have permission to manage workflow config rules";

        return [
          <Tooltip key="delete" title={tooltipTitle}>
            <span>
              <GridActionsCellItem
                icon={<Delete />}
                label="Remove Rule"
                onClick={() => {
                  if (isWorkflowConfigRuleRow(params.row)) {
                    setRuleToDelete(params.row);
                  }
                }}
                disabled={!isAuthorized}
                showInMenu={false}
              />
            </span>
          </Tooltip>,
        ];
      },
    });

    return baseColumns;
  }, [isMobile, isAuthorized, sectionExpanded, setSectionExpanded]);

  const commandButtons = useMemo(() => {
    const addButton = (
      <CommandButton
        key="add-rule"
        onClick={() => setIsAddModalOpen(true)}
        startIcon={<Add />}
        disabled={!isAuthorized}
      >
        Add Rule
      </CommandButton>
    );

    if (!isAuthorized) {
      return [
        <Tooltip
          key="add-rule-tooltip"
          title="You don't have permission to manage workflow config rules"
        >
          <span>{addButton}</span>
        </Tooltip>,
      ];
    }

    return [addButton];
  }, [isAuthorized]);

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="workflow-config-rules-table"
        isURLDriven
        columns={columns}
        dataQuery={WORKFLOW_MANAGER_CONFIG_RULES_QUERY}
        initialPageSize={100}
        commandButtons={commandButtons}
        excludeFilterColumns={[]}
        additionalFilterColumns={[
          {
            key: "facility",
            header: "Facility",
            filterKeyType: "multipleFacility",
          },
          {
            key: "tenant",
            header: "Organization",
            filterKeyType: "multipleTenant",
          },
        ]}
        initialSortModel={[
          { field: "type", sort: "asc" },
          { field: "humanReadableValue", sort: "asc" },
        ]}
        getItems={(data) => {
          const items = data.workflowManagerConfigRules.items;
          return groupRowsByType(items, sectionExpanded);
        }}
        getCount={(data) => data.workflowManagerConfigRules.count}
        getRowId={(row) => row.id}
        isRowSelectable={(params) => {
          const row = params.row;
          if (!row) return false;
          if ("__isSectionHeader" in row && row.__isSectionHeader) {
            return false;
          }
          return true;
        }}
        noDataMessage="No workflow configuration rules found."
        getRowHeight={(params: GridRowHeightParams) => {
          if (typeof params.id === "string" && params.id.startsWith("__section-header-")) {
            return 48; // Taller for section headers
          }
          return "auto";
        }}
        gridSx={{
          // Style section header rows - target by class name in first cell using :has() selector
          "& .MuiDataGrid-row:has(.section-header-row-title)": {
            backgroundColor: theme.palette.grey[200],
            borderBottom: `2px solid ${theme.palette.divider}`,
            "&:hover": {
              backgroundColor: theme.palette.grey[200],
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
              paddingTop: 1.5,
              paddingBottom: 1.5,
            },
            "& .MuiDataGrid-cell:first-of-type": {
              paddingLeft: 2,
            },
          },
          // Hide actions column by default, show on row hover
          "& .MuiDataGrid-row .MuiDataGrid-cell[data-field='actions'] div[role='menu']": {
            opacity: 0,
            transition: "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='actions'] div[role='menu']": {
            opacity: 1,
          },
        }}
        columnVisibilityModel={{
          // Hide raw columns by default for all screen sizes
          rawType: false,
          rawValue: false,
          createdAt: false,
        }}
      />
      <AddRuleModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onRuleAdded={() => {
          // Refetch is handled by the mutation in AddRuleModal
        }}
      />
      <ConfirmDialog
        open={!!ruleToDelete}
        header="Are you sure you want to remove this rule?"
        resources={
          ruleToDelete
            ? [
                {
                  resourceType: "Type",
                  resourceName: prettyPrintEnumValue(ruleToDelete.type),
                },
                {
                  resourceType: "Value",
                  resourceName: ruleToDelete.humanReadableValue || ruleToDelete.value,
                },
                ...(ruleToDelete.notes
                  ? [
                      {
                        resourceType: "Notes",
                        resourceName: ruleToDelete.notes,
                      },
                    ]
                  : []),
              ]
            : []
        }
        confirmText="Remove"
        cancelText="Cancel"
        loading={deleteLoading}
        onClose={() => setRuleToDelete(null)}
        onConfirm={handleDelete}
      />
    </Box>
  );
}
